import React, { useState, useEffect } from "react";
import { message } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useOrgUsersStore, useWashroomAuditStore } from "../../../store/store";
import { PageLoader } from "../../../components/loader/Loader";
import { useAuth } from "../../../auth/AuthProvider";
import axiosInstance from "../../../common/axiosInstance";
import axios, { AxiosProgressEvent } from "axios";
import { useS3Upload, S3TempUrl } from "../../../utils/s3Client";
import { getAllTypeList } from "../../../service/api";
import { WashroomTypeListType } from "../../../types/api";

interface Washroom {
  _id: string;
  washroomName: string;
  type: string;
  floor: string;
  premiseId: string;
}

interface ChecklistItem {
  id: string;
  question: string;
  type: string;
  weightage: number;
  categoryId: string;
}

interface Checklist {
  _id: string;
  name: string;
  premisesId: string;
  washroomType: string;
  items: ChecklistItem[];
}

const NOT_APPLICABLE_VALUE = "not_applicable" as const;
type ChecklistAnswerValue =
  | boolean
  | number
  | typeof NOT_APPLICABLE_VALUE
  | string;

interface AnswerEntry {
  question: string;
  answer: ChecklistAnswerValue;
}

interface AuditFormData {
  answers: Record<string, AnswerEntry>;
  photos: string[];
  remarks: string;
  finalScore: number | null;
}

const WashroomAudits: React.FC = () => {
  const { auditId } = useParams<{ auditId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    loading: auditLoading,
    error: auditError,
    currentAudit: currentAuditData,
    fetchAuditById,
  } = useWashroomAuditStore();
  const { uploadFileToS3 } = useS3Upload();
  const { getsignedurl } = S3TempUrl();
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();
  const isAdmin =
    user?.roles.includes("orgadmin") || user?.roles.includes("superadmin");
  const isAssigned = user?.id === currentAuditData?.assignedTo;

  const canEdit = isAssigned && !isAdmin;

  const [uploading, setUploading] = useState(false);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [signedImgUrls, setSignedImgUrls] = useState<string[]>([]);
  const [washroomTypeList, setWashroomTypeList] = useState<
    WashroomTypeListType[]
  >([]);

  const [formData, setFormData] = useState<AuditFormData>({
    answers: {},
    photos: currentAuditData?.photos || [],
    remarks: currentAuditData?.remarks || "",
    finalScore: currentAuditData?.score || null,
  });

  useEffect(() => {
    if (auditId) {
      fetchAuditById(auditId);
      if (!orgUsersData.length) fetchOrgUsers();
    }
  }, [auditId, fetchAuditById]);

  useEffect(() => {
    if (currentAuditData) {
      const normalizedAnswers = currentAuditData.answers
        ? Object.entries(currentAuditData.answers).reduce(
            (acc, [key, value]) => {
              acc[key] = {
                question:
                  currentAuditData.checklist.find(
                    (item: any) => item.id === key,
                  )?.question || "",
                answer:
                  typeof value === "object" &&
                  value !== null &&
                  "answer" in (value as any)
                    ? (value as any).answer
                    : value,
              };
              return acc;
            },
            {} as Record<string, AnswerEntry>,
          )
        : {};

      setFormData({
        answers: normalizedAnswers,
        photos: currentAuditData.photos || [],
        remarks: currentAuditData.remarks || "",
        finalScore: currentAuditData.score || null,
      });
    }
  }, [currentAuditData]);

  useEffect(() => {
    if (!formData.photos?.length) {
      setSignedImgUrls([]);
      return;
    }

    const fetchSignedUrls = async () => {
      try {
        const urls: string[] = await Promise.all(
          formData.photos.map(async (photoKey) => {
            const { fileName, fileType } = parsePhotoKey(photoKey);
            const signedUrl = await getsignedurl(fileName, fileType);
            return signedUrl;
          }),
        );
        setSignedImgUrls(urls);
      } catch (error) {
        setSignedImgUrls([]);
      }
    };

    fetchSignedUrls();
  }, [formData.photos]);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await getAllTypeList();
        if (res.data?.data && Array.isArray(res.data.data)) {
          setWashroomTypeList(res.data.data);
        } else {
          setWashroomTypeList([]);
        }
      } catch (error) {
        console.error("Failed to fetch washroom types:", error);
      }
    };
    fetchTypes();
  }, []);

  const parsePhotoKey = (
    photoString: string,
  ): { fileName: string; fileType: string } => {
    const [fileName, fileType] = photoString.split("|");
    return { fileName, fileType };
  };

  const handleChecklist = (
    key: string,
    question: string,
    value: ChecklistAnswerValue,
  ) => {
    if (!canEdit) return;
    // setChecklist((prev: any) => ({ ...prev, [key]: value }));
    setFormData((prev) => ({
      ...prev,
      answers: {
        ...prev.answers,
        [key]: {
          question: question,
          answer: value,
        },
      },
    }));
  };

  const handleRemarksChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!canEdit) return;
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, remarks: value }));
  };

  // const handleRatingChange = (num: number) => {
  //   if (!canEdit) return;
  //   setFormData((prev) => ({ ...prev, rating: num }));
  // };

  const handlePhotoUpload = async (file: File) => {
    if (!canEdit) return;
    try {
      setUploading(true);
      if (!file) {
        message.error("No file selected");
        return;
      }
      const response = await axiosInstance.post(
        `/api/S3preSignedUrl`,
        {
          fileName: file.name,
          fileType: file.type,
        },
      );
      const url = response.data.data;

      const uploadedFile = await axios.put(url, file, {
        headers: {
          "Content-Type": file.type,
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100,
            );
          } else {
          }
        },
      });
      if (uploadedFile.status === 200) {
        message.success("Photo uploaded successfully");

        const signedUrl = await getsignedurl(file.name, file.type);
        setFormData((prev) => ({
          ...prev,
          photos: [...(prev.photos || []), `${file.name}|${file.type}`],
        }));
        setSignedImgUrls((prev: string[]) => [
          ...(Array.isArray(prev) ? prev : []),
          signedUrl,
        ]);
      } else {
        message.error("Failed to upload photo");
      }
    } catch (error) {
      message.error("Failed to upload photo");
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = (index: number) => {
    if (!canEdit) return;
    // setPhotos((prev) => prev.filter((_, i) => i !== index));
    setFormData((prev) => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index),
    }));
    setSignedImgUrls((prev) => prev.filter((_, i) => i !== index));
    // setPhotoFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;
    setSubmitLoading(true);
    const payload = {
      answers: formData.answers,
      photos: formData.photos,
      remarks: formData.remarks,
      // photoFiles: photoFiles,
    };

    const response = await axiosInstance.post(
      `/api/washroom-audit/${auditId}/submit`,
      payload,
    );

    if (response.status === 200) {
      message.success("Audit submitted successfully");
      setTimeout(() => {
        navigate(`/washroom-management/audits`);
      }, 1500);
    } else {
      message.error("Failed to submit audit");
      setSubmitLoading(false);
    }
    setSubmitLoading(false);
  };

  if (auditLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <PageLoader />
      </div>
    );
  }

  if (auditError) {
    return (
      <div className="flex justify-center items-center h-screen">
        {auditError}
      </div>
    );
  }

  // if (!currentAudit) {
  //   return (
  //     <div className="flex justify-center items-center h-screen">
  //       No audit found
  //     </div>
  //   );
  // }

  return (
    <div className="px-2 py-4 max-w-7xl mx-auto mt-14  min-h-100vh">
      {/* <div className="flex flex-col sm:flex-row items-center justify-between mb-8"> */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-3 sm:mb-0 tracking-tight">
          {/* Daily Audit */}
          Washroom Audit
        </h2>
        <button
          className="flex items-center justify-center font-semibold gap-2 bg-white text-[#0fa9a6] !border !border-[#0fa9a6] px-4 py-2 rounded-full hover:!bg-[#0fa9a6] hover:!border-teal-700 hover:text-white transition"
          onClick={() => {
            currentAuditData?.status === "completed"
              ? navigate("/washroom-management/audits/history")
              : navigate("/washroom-management/audits");
          }}
        >
          BACK
        </button>
      </div>
      {/* {typeof currentAuditData?.score === "number" && (
          <> */}
      {/* <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-lg text-lg font-bold shadow">
                Audit Score: <span className="ml-2">{currentAuditData.score.toFixed(1)}%</span>
              </div> */}
      {/* <span className="ml-3 border-l-2 border-teal-400 pl-3 text-lg font-semibold text-teal-700">
                Score: {currentAuditData.score.toFixed(1)}%
              </span> */}
      {/* </>
          )} */}
      {/* </div> */}
      <form onSubmit={handleSubmit}>
        <section className="mb-6">
          <div className="bg-gray-50 border-t-4 border-teal-400 shadow rounded p-4 flex flex-col sm:flex-row items-center justify-between gap-6 sm:gap-0">
            <div className="flex-1 flex flex-col items-center sm:items-start text-center sm:text-left">
              <div className="text-xs uppercase text-gray-600 mb-1">
                Audit Score
              </div>
              <div className="text-3xl font-bold text-teal-700 tracking-tight">
                {typeof currentAuditData?.score === "number" &&
                currentAuditData?.score >= 0
                  ? currentAuditData.score.toFixed(1) + "%"
                  : "--"}
              </div>
            </div>

            <div className="hidden sm:block h-10 w-px bg-gray-200 mx-4" />

            <div className="flex-1 flex flex-col items-center sm:items-start text-center sm:text-left">
              <div className="text-xs uppercase text-gray-600 mb-1">
                Audit Date
              </div>
              <div className="text-lg text-gray-800 font-semibold">
                {currentAuditData?.auditDate ? (
                  new Date(currentAuditData.auditDate).toLocaleDateString()
                ) : (
                  <span className="text-gray-400">--</span>
                )}
              </div>
            </div>

            <div className="hidden sm:block h-10 w-px bg-gray-200 mx-4" />

            <div className="flex-1 flex flex-col items-center sm:items-start text-center sm:text-left">
              <div className="text-xs uppercase text-gray-600 mb-1">
                Audit Performed By
              </div>
              <div className="text-lg text-gray-800 font-semibold">
                {(() => {
                  const assignedUser = orgUsersData.find(
                    (u: any) => u._id === currentAuditData?.assignedTo,
                  );
                  return assignedUser ? (
                    assignedUser.firstName + " " + assignedUser.lastName
                  ) : (
                    <span className="text-gray-400">--</span>
                  );
                })()}
              </div>
            </div>

            <div className="hidden sm:block h-10 w-px bg-gray-200 mx-4" />

            <div className="flex-1 flex flex-col items-center sm:items-start text-center sm:text-left">
              <div className="text-xs uppercase text-gray-600 mb-1">
                Audit Status
              </div>
              <div className="text-lg text-gray-800 font-semibold">
                {currentAuditData?.status || (
                  <span className="text-gray-400">--</span>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Washroom Information */}
        <section className="card p-6 mb-6 shadow-sm rounded-lg bg-white">
          <h2 className="font-semibold text-lg mb-3 text-gray-700">
            Washroom Information
          </h2>
          <dl className="grid grid-cols-1 sm:grid-cols-4 gap-6 bg-gray-50 p-4 rounded-lg">
            <div>
              <dt className="text-xs font-semibold text-gray-500 uppercase mb-1">
                Washroom Name
              </dt>
              <dd className="text-base text-gray-800 font-medium">
                {currentAuditData?.washroom?.washroomName || (
                  <span className="text-gray-400">--</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-gray-500 uppercase mb-1">
                Type
              </dt>
              <dd className="text-base text-gray-800 font-medium">
                {washroomTypeList.find(
                  (type) => type._id === currentAuditData?.washroom?.type,
                )?.typeName || <span className="text-gray-400">--</span>}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-gray-500 uppercase mb-1">
                Floor
              </dt>
              <dd className="text-base text-gray-800 font-medium">
                {currentAuditData?.washroom?.floor || (
                  <span className="text-gray-400">--</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-semibold text-gray-500 uppercase mb-1">
                Premise Name
              </dt>
              <dd className="text-base text-gray-800 font-medium">
                {currentAuditData?.premises?.premisesName || (
                  <span className="text-gray-400">--</span>
                )}
              </dd>
            </div>
          </dl>
        </section>

        {/* Checklist */}
        <section className="card p-6 mb-6 shadow-sm rounded-lg bg-white">
          <h2 className="font-semibold text-lg mb-6 border-b border-gray-200 pb-2">
            Checklist
          </h2>
          <div className="space-y-4">
            {currentAuditData?.checklist &&
            currentAuditData?.checklist.length &&
            currentAuditData?.checklist.length > 0 ? (
              currentAuditData?.checklist.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between gap-4 p-3 border rounded-md hover:shadow-md transition-shadow duration-200"
                >
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-800 text-base mb-1">
                      {item.question}
                    </h4>
                    <p className="text-sm text-gray-500">
                      Weightage: {item.weightage}%
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {(() => {
                      const selectedAnswer =
                        formData?.answers?.[item.id]?.answer;

                      if (item.type === "yesno") {
                        return (
                          <>
                            {["true", "false"].map((val: string) => {
                              const boolVal = val === "true";
                              const selected = selectedAnswer === boolVal;
                              return (
                                <button
                                  type="button"
                                  key={val}
                                  className={`btn px-5 py-1.5 rounded-md font-semibold transition-colors duration-200 
                                    ${
                                      selected
                                        ? "!bg-teal-500 !text-white"
                                        : "!bg-white !text-gray-600 hover:!bg-gray-100"
                                    }`}
                                  onClick={() =>
                                    handleChecklist(
                                      item.id,
                                      item.question,
                                      boolVal,
                                    )
                                  }
                                  aria-pressed={selected}
                                >
                                  {boolVal ? "Yes" : "No"}
                                </button>
                              );
                            })}
                            <button
                              type="button"
                              className={`btn px-4 py-1.5 rounded-md font-semibold transition-colors duration-200 border
                                ${
                                  selectedAnswer === NOT_APPLICABLE_VALUE
                                    ? "!bg-teal-100 !border-teal-500 !text-teal-700"
                                    : "!bg-white !text-gray-600 !border-gray-300 hover:!bg-gray-100"
                                }`}
                              onClick={() =>
                                handleChecklist(
                                  item.id,
                                  item.question,
                                  NOT_APPLICABLE_VALUE,
                                )
                              }
                              aria-pressed={
                                selectedAnswer === NOT_APPLICABLE_VALUE
                              }
                            >
                              Not Applicable
                            </button>
                          </>
                        );
                      }

                      return (
                        <>
                          <div className="flex gap-2">
                            {[1, 2, 3, 4, 5].map((num) => {
                              const selected = selectedAnswer === num;
                              return (
                                <button
                                  key={num}
                                  type="button"
                                  className={`w-9 h-9 rounded-full flex items-center justify-center border-2 text-sm font-semibold transition-colors duration-200 
                                    ${
                                      selected
                                        ? "!bg-teal-500 !border-teal-500 !text-white"
                                        : "!bg-white !border-gray-300 !text-gray-600 hover:!bg-gray-100"
                                    }`}
                                  onClick={() =>
                                    handleChecklist(item.id, item.question, num)
                                  }
                                  aria-pressed={selected}
                                  aria-label={`Set rating to ${num}`}
                                >
                                  {num}
                                </button>
                              );
                            })}
                          </div>
                          <button
                            type="button"
                            className={`btn px-4 py-1.5 rounded-md font-semibold transition-colors duration-200 border
                              ${
                                selectedAnswer === NOT_APPLICABLE_VALUE
                                  ? "!bg-teal-100 !border-teal-500 !text-teal-700"
                                  : "!bg-white !text-gray-600 !border-gray-300 hover:!bg-gray-100"
                              }`}
                            onClick={() =>
                              handleChecklist(
                                item.id,
                                item.question,
                                NOT_APPLICABLE_VALUE,
                              )
                            }
                            aria-pressed={
                              selectedAnswer === NOT_APPLICABLE_VALUE
                            }
                          >
                            Not Applicable
                          </button>
                        </>
                      );
                    })()}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-gray-400 italic select-none">
                No checklist items found for this washroom type.
              </div>
            )}
          </div>
        </section>

        {/* Rating & Photo Upload */}
        <div className="flex flex-col md:flex-row gap-6 mb-6">
          {/* Overall Rating */}
          {/* <div className="card p-4 flex-1 mb-4 md:mb-0">
            <div className="font-bold text-lg mb-4">Overall Rating</div>
            <div className="flex justify-center gap-4">
              {[1, 2, 3, 4, 5].map((num) => (
                <button
                  key={num}
                  type="button"
                  className={`w-10 h-14 mt-4 rounded-full border-2 border-gray-300 flex items-center justify-center text-lg font-semibold ${
                    rating === num
                      ? "bg-[#0fa9a6] text-white border-[#0fa9a6]"
                      : "bg-white"
                  }`}
                  onClick={() => setRating(num)}
                >
                  {num}
                </button>
              ))}
            </div>
          </div> */}

          {/* Upload Photo */}
          <div className="card p-4 flex-1">
            <h2 className="font-semibold text-lg mb-4">Photos</h2>
            {canEdit && (
              <label
                className={`flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg h-32 cursor-pointer bg-gray-50 mb-4
              ${
                uploading
                  ? "opacity-50 cursor-not-allowed pointer-events-none"
                  : ""
              }`}
                tabIndex={uploading ? -1 : 0}
              >
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  multiple
                  disabled={uploading || !canEdit}
                  onChange={(e) => {
                    if (!uploading && e.target.files) {
                      handlePhotoUpload(e.target.files[0]);
                    }
                    e.target.value = "";
                  }}
                />
                <span className="text-gray-500 select-none">
                  {!canEdit
                    ? "View only — uploads disabled"
                    : "Click to upload photo."}
                </span>
              </label>
            )}

            {canEdit && (
              <button
                type="button"
                className="btn btn-primary mb-4"
                disabled={uploading}
                onClick={() => {
                  const input = document.querySelector(
                    'input[type="file"]:not(:disabled)',
                  ) as HTMLInputElement | null;
                  input?.click();
                }}
              >
                {uploading ? "Uploading..." : "Upload"}
              </button>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {signedImgUrls &&
                signedImgUrls.map((url, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={url}
                      alt={`Photo ${index + 1}`}
                      className="w-full h-32 object-cover rounded"
                    />
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => handleRemovePhoto(index)}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 opacity-80 group-hover:opacity-100 transition-opacity"
                        aria-label={`Remove Photo ${index + 1}`}
                        tabIndex={!canEdit ? -1 : 0}
                      >
                        <DeleteOutlined />
                      </button>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Remarks */}
        <section className="card p-4 mb-6">
          <h2 className="font-semibold text-lg mb-2">Remarks</h2>
          <textarea
            rows={4}
            disabled={!canEdit}
            className="form-control min-h-[100px] border border-gray-300 rounded p-2 w-full"
            placeholder="Add any additional notes or observations..."
            value={formData.remarks || ""}
            onChange={handleRemarksChange}
          />
        </section>

        {/* Submit Button */}
        {canEdit && (
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!canEdit || submitLoading}
              className="btn btn-primary roundedBt px-8 py-2 text-white text-base font-semibold"
            >
              {submitLoading ? "Submitting..." : "Submit Audit"}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default WashroomAudits;
